import { useState, useEffect } from 'react';
import { clabsApiClient } from '../../../services/clabsApiService';
import { apiClient } from '../../../services/apiClient';
import PortalModal from '../../common/PortalModal/PortalModal';
import Input from '../../common/Input/Input';
import Button from '../../common/Button/Button';
import ToggleSwitch from '../../common/ToggleSwitch/ToggleSwitch';
import '../../Organization/InformationSources.css'; // Reuse selectable card styles
import { config } from '../../../config/envConfig';

function ClabsModal({ isOpen, onClose, onSave, initialConfig = null }) {
  // Local state - no name field needed since one config per user
  const [clabsConfig, setClabsConfig] = useState({
    apiUrl: config.CLABS_API_URL,
    selectedSources: [],
    availableSources: [],
    enabled: true
  });
  const [clabsConnectionStatus, setClabsConnectionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  // Initialize config when modal opens
  useEffect(() => {
    if (isOpen) {
      if (initialConfig) {
        // Load existing configuration - handle both nested clabsConfig and flat format
        const configData = initialConfig.clabsConfig || initialConfig;
        
        const loadedConfig = {
          apiUrl: configData.api_url || configData.apiUrl || 'http://localhost:8080',
          selectedSources: configData.selected_sources || configData.selectedSources || [],
          availableSources: configData.available_sources || configData.availableSources || [],
          enabled: typeof configData.enabled === 'boolean' ? configData.enabled : true
        };
        
        console.log('📥 Loading Clabs configuration:', {
          enabled: loadedConfig.enabled,
          raw_enabled: configData.enabled,
          apiUrl: loadedConfig.apiUrl,
          selectedSources: loadedConfig.selectedSources
        });
        
        setClabsConfig(loadedConfig);
        setHasExistingConfig(true);
        
        // If we have available sources, mark as connected
        if ((configData.available_sources || configData.availableSources) && 
            (configData.available_sources || configData.availableSources).length > 0) {
          setClabsConnectionStatus('connected');
        }
      } else {
        console.log('📝 Creating new Clabs configuration with enabled=true');
        // Reset to defaults for new configuration
        setClabsConfig({
          apiUrl: 'http://localhost:8080',
          selectedSources: [],
          availableSources: [],
          enabled: true
        });
        setClabsConnectionStatus(null);
        setHasExistingConfig(false);
      }
    }
  }, [isOpen, initialConfig]);

  const handleTestConnection = async () => {
    try {
      setClabsConnectionStatus('testing');
      setLoading(true);
      
      // Update Clabs API client URLs with current config
      if (clabsConfig.apiUrl) {
        clabsApiClient.updateUrls(clabsConfig.apiUrl);
      }
      
      // Test connection and get available sources
      const connectionResult = await clabsApiClient.testConnection();
      
      if (connectionResult.success) {
        const availableSources = await clabsApiClient.getAvailableSources();
        setClabsConnectionStatus('connected');
        
        // Update config with available sources
        setClabsConfig(prev => ({
          ...prev,
          availableSources: availableSources
        }));
        
      } else {
        setClabsConnectionStatus('failed');
        alert(`Connection failed: ${connectionResult.message}`);
      }
    } catch (error) {
      console.error('Clabs connection test failed:', error);
      setClabsConnectionStatus('failed');
      alert(`Connection test failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceSelection = (source, isSelected) => {
    const newSelected = isSelected
      ? [...clabsConfig.selectedSources, source]
      : clabsConfig.selectedSources.filter(s => s !== source);
    
    setClabsConfig(prev => ({
      ...prev,
      selectedSources: newSelected
    }));
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!clabsConfig.apiUrl) {
        alert('Please provide a valid API URL');
        return;
      }
      
      if (clabsConnectionStatus !== 'connected') {
        alert('Please test the connection successfully before saving');
        return;
      }
      
      if (clabsConfig.selectedSources.length === 0) {
        alert('Please select at least one source');
        return;
      }
      
      setLoading(true);
      
      // Prepare configuration data for backend
      const configData = {
        api_url: clabsConfig.apiUrl,
        selected_sources: clabsConfig.selectedSources,
        available_sources: clabsConfig.availableSources,
        connection_status: 'connected',
        description: 'User Clabs configuration',
        tags: ['clabs', 'external'],
        is_active: true,
        enabled: clabsConfig.enabled
      };
      
      console.log('💾 Saving Clabs configuration:', {
        enabled: configData.enabled,
        is_active: configData.is_active,
        selected_sources: configData.selected_sources
      });
      
      // Save configuration (upsert)
      const result = await apiClient.saveClabsConfig(configData);
      
      if (result.success) {
        // Call the parent's save handler with the configuration
        onSave({
          ...clabsConfig,
          id: result.config_id,
          connection_status: 'connected'
        });
        onClose();
      } else {
        alert('Failed to save configuration: ' + result.message);
      }
      
    } catch (error) {
      console.error('Error saving Clabs configuration:', error);
      alert('Error saving configuration: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setClabsConnectionStatus(null);
    onClose();
  };

  const footerContent = (
    <>
      <Button variant="ghost" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={
          loading ||
          !clabsConfig.apiUrl ||
          clabsConnectionStatus !== 'connected' ||
          clabsConfig.selectedSources.length === 0
        }
      >
        {hasExistingConfig ? 'Update Configuration' : 'Save Configuration'}
      </Button>
    </>
  );

  return (
    <PortalModal
      isOpen={isOpen}
      onClose={handleClose}
      title={hasExistingConfig ? 'Update Clabs Source' : 'Configure Clabs Source'}
      footer={footerContent}
      size="medium"
    >
      <div className="form-grid">
        {/* API URL Input */}
        <Input
          label="Clabs API URL"
          value={clabsConfig.apiUrl}
          onChange={(e) => setClabsConfig(prev => ({ ...prev, apiUrl: e.target.value }))}
          placeholder="http://localhost:8080"
          disabled={loading}
          helperText="Enter the URL of your Clabs instance API endpoint"
        />

        {/* Enabled Toggle */}
        <div className="ui-form-group" style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-surface)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
          <div>
            <label className="ui-label" style={{ marginBottom: 0, color: 'var(--text-primary)' }}>Use Clabs as Context</label>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Enable to use Clabs for answering queries</p>
          </div>
          <ToggleSwitch
            checked={clabsConfig.enabled}
            onChange={(val) => setClabsConfig(prev => ({ ...prev, enabled: val }))}
            disabled={loading}
          />
        </div>

        {/* Connection Test Section */}
        <div className="ui-form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className="ui-label">Connection Status</label>
            {clabsConnectionStatus === 'connected' && (
              <span style={{ color: 'var(--success, #10b981)', fontSize: '0.85rem' }}>
                <i className="fas fa-check-circle"></i> Connected
              </span>
            )}
            {clabsConnectionStatus === 'failed' && (
              <span style={{ color: 'var(--danger, #ef4444)', fontSize: '0.85rem' }}>
                <i className="fas fa-exclamation-circle"></i> Failed
              </span>
            )}
          </div>
          <Button
            variant="secondary"
            className="full-width"
            onClick={handleTestConnection}
            disabled={!clabsConfig.apiUrl || clabsConnectionStatus === 'testing'}
            icon={clabsConnectionStatus === 'testing' ? 'fas fa-spinner fa-spin' : 'fas fa-plug'}
          >
            {clabsConnectionStatus === 'testing' ? 'Testing Connection...' : 'Test Connection'}
          </Button>
        </div>

        {/* Sources Selection Grid (Only show if connected) */}
        {clabsConnectionStatus === 'connected' && (
          <div className="ui-form-group">
            <label className="ui-label">
              Available Sources ({clabsConfig.selectedSources.length} selected)
            </label>
            
            {clabsConfig.availableSources.length > 0 ? (
              <div className="engines-grid">
                {clabsConfig.availableSources.map(source => {
                  const isSelected = clabsConfig.selectedSources.includes(source);
                  return (
                    <div
                      key={source}
                      className={`engine-card ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSourceSelection(source, !isSelected)}
                      title={source}
                    >
                      {isSelected && <i className="fas fa-check"></i>}
                      <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        maxWidth: '100%'
                      }}>
                        {source}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state" style={{ 
                padding: '2rem', 
                textAlign: 'center',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '8px',
                border: '1px solid var(--border-subtle)'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ 
                  fontSize: '1.5rem', 
                  marginBottom: '0.5rem',
                  color: 'var(--text-secondary)',
                  display: 'block'
                }}></i>
                <p style={{ margin: 0, color: 'var(--text-secondary)' }}>
                  No sources found in this Clabs instance.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </PortalModal>
  );
}

export default ClabsModal;
